'use client'

import {forwardRef, useRef, useState, useEffect, ChangeEventHandler, useContext, ReactNode, SelectHTMLAttributes, PropsWithChildren} from 'react'
import {cx} from '@local/utils/cx'

import ChevronSVG from '@local/assets/svgs/chevron.svg'
import {FormContext} from '@local/stores/contexts'

export type SelectOptionType = {label: string, value: string, disabled?: boolean, selected?: boolean}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
	name: string,
	className?: string,
	containerClassName?: string,
	selectClassName?: string,
	titleClassName?: string,
	chevronClassName?: string,
	labelClassName?: string,
	options: SelectOptionType[],
	required?: boolean,
	label?: string,
	id?: string,
	placeholder?: string,
	disablePlaceholder?: boolean,
	noFocus?: boolean,
	variant?: 'default',
	theme?: 'light' | 'dark' | 'dark-grey',
	floatLabel?: boolean,
	floatError?: boolean,
	error?: boolean,
	footer?: boolean,
	errorMessage?: string,
	onChange?: ChangeEventHandler,
	defaultValue?: string,
	prefixIcon?: ReactNode,
	keepPrefixIcon?: boolean,
	children?: ReactNode,
}

export const Select = forwardRef<HTMLSelectElement, PropsWithChildren<SelectProps>>(({
	name,
	className,
	containerClassName,
	selectClassName,
	titleClassName,
	chevronClassName,
	labelClassName,
	options = [],
	required = false,
	noFocus = false,
	label,
	id,
	placeholder,
	disablePlaceholder = true,
	theme = 'light',
	floatLabel = true,
	floatError = true,
	error = false,
	footer = false,
	errorMessage,
	onChange,
	defaultValue,
	prefixIcon,
	keepPrefixIcon = false,
	children,
	...props
}: SelectProps, ref) => {
	const selectRef = useRef(null)
	const valueRef = useRef(null)
	const {hasValidation} = useContext(FormContext)

	const [selected, setSelected] = useState(null)
	const [title, setTitle] = useState(placeholder || options[0].name)

	placeholder = placeholder && required ? (placeholder + '*') : placeholder

	useEffect(() => {
		setTitle(selectRef.current.options[selectRef.current.options.selectedIndex].textContent.trim())
	}, [selected, onChange])

	useEffect(() => {
		if (selectRef.current.form) selectRef.current.form.addEventListener('reset', handleResetForm)

		return () => {
			if (selectRef.current && selectRef.current.form) selectRef.current.form.removeEventListener('reset', handleResetForm)
		}
	}, [])

	const handleResetForm = () => {
		setTitle(placeholder)
		setSelected(null)
	}

	const handleChange = (e) => {
		setSelected(selectRef.current.value)
		onChange?.(e)
	}

	return (
		<div
			ref={ref}
			className={cx(
				'text-11 border-b border-white/[15%]',
				'relative inline-block',
				(!floatError && error) && 'mb-28',
				containerClassName,
			)}>
			<select
				ref={selectRef}
				id={id || name}
				name={name}
				required={required}
				className={cx(
					'text-11',
					selectClassName,
					'absolute inset-0',
					'h-44 appearance-none focus:outline-none focus:ring-0',
					'opacity-0 cursor-pointer',
					'px-10 py-8',
					'peer',
				)}
				onChange={handleChange}
				aria-label={label || placeholder}
				defaultValue={defaultValue || undefined}
				{...props}>
				{placeholder && <option value='' disabled={disablePlaceholder}>{placeholder}</option>}
				{options && options.map((item, index) => (
					<option
						key={`${item.value}-${index}`}
						defaultChecked={item.selected}
						value={item.value}>
						{item.label}
					</option>
				))}
			</select>
			<span
				ref={valueRef}
				className={cx(
					'flex items-center pointer-events-none border-0',
					'whitespace-nowrap',
					'transition-[color,box-shadow,border] duration-300',
					'h-44 rounded-xxs px-10 text-11',
					!selected && 'uppercase',
					theme === 'light' && 'text-black bg-white border border-grey-1',
					theme === 'dark-grey' && 'text-white bg-grey-4 border border-grey-2',
					error && 'text-red',
					!error && ((theme === 'dark' || theme === 'dark-grey') ? 'text-white' : 'text-black'),
					!noFocus && 'peer-focus:ring-1 peer-focus:ring-inset peer-focus:ring-grey-2',
					hasValidation && 'peer-invalid:text-red peer-invalid:placeholder:text-red',
					className,
				)}>
				{(keepPrefixIcon || !selected) && prefixIcon}
				<span key={title} className={cx('mt-2', titleClassName)}>{title}</span>
				{label &&
					<label
						htmlFor={name}
						className={cx(
							'order-first duration-300',
							labelClassName,
							floatLabel ? 'absolute ml-1 px-4 transform -translate-y-1/2 z-2 origin-left peer-focus:top-0 pointer-events-none' : 'mr-24 pb-0',
							selected ? 'top-0' : 'top-1/2',
							'left-12',
							theme === 'dark' && 'bg-black',
							theme === 'dark-grey' && 'bg-grey-4',
							theme === 'light' && 'bg-white',
							footer && 'text-white',
							error && 'text-red',
							error && theme === 'light' && 'text-red',
						)}>
						{label}{required && '*'}
					</label>}
				{children || (
					<div className={cx(
						'flex items-center justify-center shrink-0 rounded ml-auto',
						chevronClassName,
						theme === 'light' && 'bg-grey-stroke',
					)}>
						<ChevronSVG className={cx(
							'w-8 lg:w-9 m-auto',
							theme === 'dark' && 'text-white',
							theme === 'dark-grey' && 'text-white',
							theme === 'light' && 'text-black',
						)} />
					</div>
				)}
			</span>
			{errorMessage && (
				<span
					className={cx(
						'absolute top-full',
						floatError ? 'px-4 mr-1 -translate-y-1/2' : 'pt-4 left-0',
						'text-11 text-red duration-300 transition-opacity pointer-events-none',
						'right-12',
						theme === 'dark' && 'bg-black',
						theme === 'dark-grey' && 'bg-grey-4',
						theme === 'light' && 'bg-white',
						error ? 'opacity-100' : 'opacity-0',
					)}>
					{errorMessage}
				</span>
			)}
		</div>
	)
})
